import { damp } from '../core/math';
/* ============================================================
   掃描艙的鏡頭控制：自由旋轉 + 四顆快捷視角
   ============================================================

   B3 拍板（2026-08-03）：拖曳／滾輪保留，另加 3/4・正・側・背四顆快捷視角；
   閃到快捷視角就停自轉，一拖曳就回到自由模式。

   ⚠️ **旋轉一律由鏡頭負責，展品不自己轉。**
   B3 之前 `setCodexYaw()` 轉展品、`placeCodexCamera()` 又讓鏡頭繞同一個 yaw
   公轉 —— 兩者**完全抵消**，拖曳只把房間（立柱、比例尺機體、燈）轉走，
   展品永遠正面對著鏡頭。實測：拖 180° 之後 KEIL 的斜面仍然朝著鏡頭。
   修掉之後「正面／側面／背面」才有意義。

   `VIEW` 是當前值、`TGT` 是目標值，damp 追上去（跟機庫同一套）。 */

export var VIEW = { yaw: 0.62, pitch: 0.17 };
var TGT = { yaw: 0.62, pitch: 0.17 };

/** 四顆快捷視角。`q`（3/4）是預設，也是 68 隻逐隻截圖比對時唯一該用的那一個
    —— 每張角度不一樣的話「逐隻比對」根本不成立。 */
var VIEWS: any = {
  q: { yaw: 0.62, pitch: 0.17 },
  f: { yaw: 0, pitch: 0.10 },
  s: { yaw: Math.PI / 2, pitch: 0.10 },
  b: { yaw: Math.PI, pitch: 0.14 }
};

var lock: string | null = 'q';
var idle = 0, drag = false, lastX = 0, lastY = 0, zoom = 1;

export function codexZoom() { return zoom; }
/** 換展品／有人操作時把自轉的計時歸零。 */
export function pokeCodexView() { idle = 0; }

function syncButtons() {
  var host = typeof document === 'undefined' ? null : document.getElementById('cViews');
  if (!host) return;
  var bs = host.querySelectorAll('button');
  for (var i = 0; i < bs.length; i++) {
    bs[i].classList.toggle('on', bs[i].getAttribute('data-v') === lock);
  }
}

export function setCodexView(id) {
  var V = VIEWS[id];
  if (!V) return;
  lock = id;
  TGT.yaw = V.yaw; TGT.pitch = V.pitch;
  idle = 0;
  syncButtons();
}

/** 每次開啟圖鑑都回到 3/4。**起始角度是固定的**（不是取自時鐘）——
    產品畫面的第一印象不能是隨機的（機庫那一輪記過）。 */
export function resetCodexView() {
  zoom = 1; idle = 0; lock = 'q';
  VIEW.yaw = TGT.yaw = VIEWS.q.yaw;
  VIEW.pitch = TGT.pitch = VIEWS.q.pitch;
  syncButtons();
}

/** 每個畫面幀。回傳的是要餵給 `placeCodexCamera()` 的角度。 */
export function tickCodexView(dt) {
  idle += dt;
  /* **鎖在快捷視角時不自轉**：逐隻截圖比對要的就是同一個角度。 */
  if (!lock && !drag && idle > 2.2) TGT.yaw += dt * 0.28;
  VIEW.yaw = damp(VIEW.yaw, TGT.yaw, 7, dt);
  VIEW.pitch = damp(VIEW.pitch, TGT.pitch, 7, dt);
}

/** 面板上的東西不該啟動拖曳旋轉（點清單、按按鈕、拉滑桿都不是在轉展品）。 */
var PANEL = '.ccol,.cfoot,.cedge,.cviews';
function onPanel(e) {
  var t = e.target;
  return !!(t && t.closest && t.closest(PANEL));
}

/** 綁拖曳／滾輪／快捷視角按鈕。`isOpen` 讓這一層不用認識圖鑑的開關狀態。

    ⚠️ **host 要綁 `#screenCodex`，不是 `#stage`。**
    `.screen` 是 `pointer-events:auto`（`style.css`），它整片蓋在 3D 畫布上 ——
    綁在 `#stage` 上的話 `pointerdown` 永遠打在 `#screenCodex` 上，
    拖曳完全沒有反應。舊版看起來會轉是**閒置自轉**造成的錯覺
    （放開滑鼠後等 2.2 秒它自己開始轉），實測 `camera.position` 在拖曳前後
    一模一樣。 */
export function bindCodexView(host, isOpen) {
  host.addEventListener('pointerdown', function (e) {
    if (!isOpen() || onPanel(e)) return;
    drag = true; lastX = e.clientX; lastY = e.clientY; idle = 0;
  });
  window.addEventListener('pointerup', function () { drag = false; });
  window.addEventListener('pointermove', function (e) {
    if (!drag || !isOpen()) return;
    /* 一動手就解鎖快捷視角。**不解鎖的話拖曳會跟鎖定值打架** ——
       damp 每幀把它拉回去，症狀是「拖得動但會彈回來」。 */
    if (lock) { lock = null; syncButtons(); }
    TGT.yaw -= (e.clientX - lastX) * 0.008;
    TGT.pitch = Math.max(-0.25, Math.min(0.85, TGT.pitch + (e.clientY - lastY) * 0.005));
    lastX = e.clientX; lastY = e.clientY; idle = 0;
  });
  host.addEventListener('wheel', function (e) {
    if (!isOpen() || onPanel(e)) return;
    e.preventDefault();
    zoom = Math.max(0.45, Math.min(2.2, zoom + (e.deltaY > 0 ? 0.09 : -0.09)));
  }, { passive: false });

  var vs = typeof document === 'undefined' ? null : document.getElementById('cViews');
  if (!vs) return;
  var bs = vs.querySelectorAll('button');
  for (var i = 0; i < bs.length; i++) {
    (function (b) { b.onclick = function () { setCodexView(b.getAttribute('data-v')); }; })(bs[i]);
  }
}
